import { IconAlertTriangle, IconPencil, IconPlus } from '@tabler/icons-react';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';

interface EditableTab {
	id: string;
	name: string;
}

interface Props {
	newButtonText: string;
	editInputPlaceholder: string;
	tabs: EditableTab[];
	activeEdit: boolean;
	setActiveEdit: (bool: boolean) => void;
	addNewTab: () => void;
	onTabNameChange: (id: string, name: string) => void;
	onRemoveTab: (id: string) => void;
	activeTab: string;
	setActiveTab: (id: string) => void;
	deleteButtonText: string;
	children?: ReactNode;
}

const EditInput = ({ tab, editInputPlaceholder, onTabNameChange }) => {
	const [tabName, setTabName] = useState(tab.name);

	return (
		<div className="flex items-center px-3 py-2.5 border-b box-border border-solid border-b-gray-800 ">
			<input
				className="text-lg font-bold bg-transparent border-none w-[210px]"
				placeholder={editInputPlaceholder}
				value={tabName}
				onChange={(event) => setTabName(event.target.value)}
				onKeyDown={(event) => {
					if (
						(event.code === 'Enter' || event.code === 'NumpadEnter') &&
						tabName.length
					) {
						onTabNameChange(tab.id, tabName);
					}
				}}
				onBlur={() => {
					if (tabName.length) {
						onTabNameChange(tab.id, tabName);
					}
				}}
			/>
			<IconPencil size={24} className="ml-2" />
		</div>
	);
};

export const EditableTabs = ({
	newButtonText,
	tabs,
	editInputPlaceholder,
	activeEdit,
	setActiveEdit,
	addNewTab,
	onTabNameChange,
	activeTab,
	setActiveTab,
	onRemoveTab,
	deleteButtonText,
	children,
}: Props) => {
	return (
		<>
			<div className="border-b border-solid border-b-gray-400 mb-5 flex justify-between gap-1">
				<div className="flex flex-1 -mb-[1px] overflow-auto">
					{tabs.map((tab) => {
						if (activeTab === tab.id && activeEdit) {
							return (
								<EditInput
									key={tab.id}
									tab={tab}
									editInputPlaceholder={editInputPlaceholder}
									onTabNameChange={onTabNameChange}
								/>
							);
						} else {
							return (
								<div
									key={tab.id}
									onClick={() => !activeEdit && setActiveTab(tab.id)}
									className={classNames(
										'group flex items-center justify-between text-lg p-3  box-border cursor-pointer',
										{
											'font-bold border-b border-solid border-b-gray-800':
												!activeEdit && activeTab === tab.id,
										}
									)}
								>
									<div className="text-nowrap">{tab.name}</div>
									{activeTab === tab.id && (
										<IconPencil
											size={24}
											onClick={() => setActiveEdit(true)}
											className="min-w-[30px] ml-2 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-100 hover:text-blue-600"
										/>
									)}
								</div>
							);
						}
					})}
				</div>
				<div
					className="font-semibold text-blue-600 flex items-center justify-center cursor-pointer"
					onClick={addNewTab}
				>
					<IconPlus className="text-blue-600 mr-2" />
					{newButtonText}
				</div>
			</div>
			{children}
			{tabs.length > 1 && !activeEdit && (
				<div className="flex justify-center">
					<div
						className="flex items-center text-red-500 cursor-pointer"
						onClick={() => onRemoveTab(activeTab)}
					>
						<IconAlertTriangle className="mr-2" />
						<div className="font-bold text-sm">{deleteButtonText}</div>
					</div>
				</div>
			)}
		</>
	);
};
